/**
 * JWT Token Utilities
 * Handles token validation, expiration checking, and automatic logout
 */



/**
 * Decode JWT token payload
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
    try {
        if (!token) return null;
        
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        
        const payload = JSON.parse(atob(parts[1]));
        return payload;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired, false if valid
 */
export const isTokenExpired = (token) => {
    const payload = decodeToken(token);
    if (!payload || !payload.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
};

/**
 * Get token expiration time in seconds
 * @param {string} token - JWT token
 * @returns {number|null} Expiration timestamp or null
 */
export const getTokenExpiration = (token) => {
    const payload = decodeToken(token);
    return payload?.exp || null;
};

/**
 * Get remaining time until token expires
 * @param {string} token - JWT token
 * @returns {number} Remaining seconds (0 if expired)
 */
export const getTokenRemainingTime = (token) => {
    const payload = decodeToken(token);
    if (!payload || !payload.exp) return 0;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const remaining = payload.exp - currentTime;
    return Math.max(0, remaining);
};

/**
 * Check if token will expire soon (within 2 minutes)
 * @param {string} token - JWT token
 * @returns {boolean} True if expiring soon
 */
export const isTokenExpiringSoon = (token) => {
    const remainingTime = getTokenRemainingTime(token);
    return remainingTime > 0 && remainingTime < 120; // 2 minutes
};

/**
 * Validate token and handle expiration
 * @param {string} token - JWT token
 * @returns {boolean} True if valid, false if expired
 */
export const validateToken = (token) => {
    if (!token) return false;
    
    if (isTokenExpired(token)) {
        console.warn('Token has expired, logging out...');
        handleTokenExpiration();
        return false;
    }
    
    if (isTokenExpiringSoon(token)) {
        console.warn('Token will expire soon:', getTokenRemainingTime(token), 'seconds remaining');
    }
    
    return true;
};

/**
 * Handle token expiration - logout user and redirect
 */
export const handleTokenExpiration = () => {
    // Store the current path for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login' && currentPath !== '/') {
        localStorage.setItem('redirectAfterLogin', currentPath);
    }
    
    // Set session expiration flag for showing alert message
    localStorage.setItem('sessionExpired', 'true');
    
    // Remove token from localStorage
    localStorage.removeItem('token');
    
    // Clear any auth token from axios headers
    if (window.axios && window.axios.defaults) {
        delete window.axios.defaults.headers.common['Authorization'];
    }
    
    // Force a full page reload to clear Redux state and redirect
    if (window.location.pathname !== '/login') {
        console.log('Token expired, redirecting to login...');
        window.location.replace('/login');
    }
};

/**
 * Get token info for debugging
 * @param {string} token - JWT token
 * @returns {object} Token information
 */
export const getTokenInfo = (token) => {
    const payload = decodeToken(token);
    if (!payload) return { valid: false };
    
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < currentTime;
    const remainingTime = Math.max(0, payload.exp - currentTime);
    
    return {
        valid: !isExpired,
        expired: isExpired,
        issuedAt: payload.iat ? new Date(payload.iat * 1000) : null,
        expiresAt: payload.exp ? new Date(payload.exp * 1000) : null,
        remainingSeconds: remainingTime,
        remainingMinutes: Math.floor(remainingTime / 60),
        issuer: payload.iss,
        subject: payload.sub,
        payload
    };
}; 